import { useEffect, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
} from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import ErrorBoundary from "./components/ErrorBoundary";

import BackToTop from "./components/BackToTop";
import ThemeProvider from "./context/ThemeContext";
import LoadingSpinner from "./components/LoadingSpinner";
import PageTransition from "./components/PageTransition";
import ScrollToTop from "./components/ScrollToTop";
import SiteHeader from "./components/layout/SiteHeader";
import SiteFooter from "./components/layout/SiteFooter";

// Lazy load components for better performance
const Welcome = lazy(() => import("./components/Welcome"));
const Tests = lazy(() => import("./components/Tests"));
const Certificates = lazy(() =>
  import("./components/Certificates/Certificates")
);
const BugCarousel = lazy(() => import("./components/BugCarousel"));
const BuggedZone = lazy(() => import("./components/BuggedZone"));
const Postman = lazy(() => import("./components/Postman"));
const DBeaver = lazy(() => import("./components/DBeaver"));
const ProjectTesting = lazy(() => import("./components/ProjectTesting"));
const QAProjectCollection = lazy(() =>
  import("./components/QAProjectCollection/QAProjectCollection")
);

function AnimatedRoutes() {
  return (
    <AnimatePresence mode="wait">
      <Routes>
        <Route
          path="/"
          element={
            <PageTransition>
              <Welcome />
            </PageTransition>
          }
        />
        <Route
          path="/tests"
          element={
            <PageTransition>
              <Tests />
            </PageTransition>
          }
        />
        <Route
          path="/certificates"
          element={
            <PageTransition>
              <Certificates />
            </PageTransition>
          }
        />
        <Route
          path="/bugs"
          element={
            <PageTransition>
              <BugCarousel />
            </PageTransition>
          }
        />
        <Route
          path="/bugged"
          element={
            <PageTransition>
              <BuggedZone />
            </PageTransition>
          }
        />
        <Route
          path="/postman"
          element={
            <PageTransition>
              <Postman />
            </PageTransition>
          }
        />
        <Route
          path="/dbeaver"
          element={
            <PageTransition>
              <DBeaver />
            </PageTransition>
          }
        />
        <Route
          path="/project-testing"
          element={
            <PageTransition>
              <ProjectTesting />
            </PageTransition>
          }
        />
        <Route
          path="/qa-project-collection"
          element={
            <PageTransition>
              <QAProjectCollection />
            </PageTransition>
          }
        />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        document.activeElement?.blur();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <ThemeProvider>
      <ErrorBoundary>
        <div className="layout-shell">
          <a href="#main" className="skip-to-main">
            Skip to main content
          </a>
          <ScrollToTop />
          <SiteHeader />

          <main id="main" className="main-content shell-container py-6 sm:py-8">
            <Suspense fallback={<LoadingSpinner />}>
              <AnimatedRoutes />
            </Suspense>
          </main>

          <BackToTop />
          <SiteFooter />
        </div>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default function WrappedApp() {
  return (
    <Router>
      <App />
    </Router>
  );
}
